import React, { createContext, useContext, useEffect, useState } from 'react';
import { CartLocation, useCartContext } from '../../../utils/cart/context/CartContext';
import { formatMoney } from '../../../utils/FormatMoney';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';

export type BundleOffering = {
  heading: string;
  imageUrl: string;
  productHandle: string;
  variantId: number;
  price: string;
  description?: string;
  wasPrice?: string;
  tagManagerData: ProductGtmData;
  location: string;
  linkedLineItemUuid?: string;
  sku: string;
  maxQuantity: number;
  productGroup: number;
  availabilityCanPreOrder: boolean;
  availabilityFulfilment: string;
  availabilityProductLifecycle?: string;
  freightPrice: number;
};

type BundleOfferings = Array<{ containerId: string; offerings: Array<BundleOffering> }>;

export type BundleOfferingsContextProps = {
  title: string;
  ctaLabel: string;
  bundleOfferings: BundleOfferings;
};

export const BundleOfferingsContext = createContext<BundleOfferingsContextProps>(null);

export const BundleOfferingsProvider = ({ children }: { children: React.ReactNode }) => {
  const { cart, location } = useCartContext();
  const [bundleOfferings, setBundleOfferings] = useState<BundleOfferings>([]);
  const title = window.Jbhifi?.cartSuggestionStrings?.heading || '';
  const ctaLabel = window.Jbhifi?.cartSuggestionStrings?.CTA || '';

  useEffect(() => {
    if (!cart?.items?.length || (location !== CartLocation.minicart && location !== CartLocation.cart)) return;
    const updatedOfferings = cart.items
      .filter(item => item.bundleSuggestions)
      .map(({ bundleSuggestions, line_id, properties }) => {
        const containerId = `bundle-${line_id}`;
        const offerings = bundleSuggestions?.map(suggestion => {
          return {
            heading: suggestion.title,
            imageUrl: suggestion.product_image,
            productHandle: suggestion.handle,
            variantId: suggestion.variant_id,
            description: suggestion.subtitle,
            price: formatMoney(suggestion.price),
            wasPrice: suggestion.pricing.displayWasPrice ? formatMoney(suggestion.pricing.wasPrice) : null,
            tagManagerData: suggestion.google_tag_manager,
            location,
            linkedLineItemUuid: properties._uuid,
            sku: suggestion.sku,
            maxQuantity: suggestion.product?.limitPerOrder,
            productGroup: suggestion.productGroup?.id,
            availabilityCanPreOrder: suggestion.availability.canPreOrder,
            availabilityFulfilment: suggestion.availability.fulfilment,
            freightPrice: suggestion.product?.freightPrice || 0,
          } as BundleOffering;
        });

        return { containerId, offerings };
      })
      .filter(offering => offering.offerings?.length);
    setBundleOfferings(updatedOfferings as BundleOfferings);
  }, [cart, location]);

  return (
    <BundleOfferingsContext.Provider value={{ title, ctaLabel, bundleOfferings }}>
      {children}
    </BundleOfferingsContext.Provider>
  );
};

export const useBundleOfferings = () => {
  const context = useContext(BundleOfferingsContext);
  if (context === undefined || context === null) {
    throw new Error('useBundleOfferings must be used within a BundleOfferingsProvider');
  }
  return context;
};
